// Week sanity checks for the games importer.
//
// The importer's date range defaults to the coming Wed–Tue, but the target
// week defaults to whatever week Game Management is on. On 2026-10-11 that
// put seven week 7 games into week 6. These helpers pick a better default and
// spot a mismatch before anything is written, using only game kickoffs the
// importer already has (no stored week dates).

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Normalizes a game's startTime (Firestore Timestamp, {seconds}, Date, or ISO
 * string) to a Date. Returns null when it is missing or unparseable.
 */
export const toDate = (startTime) => {
  if (!startTime) return null;
  let date;
  if (typeof startTime.toDate === 'function') date = startTime.toDate();
  else if (typeof startTime.seconds === 'number') date = new Date(startTime.seconds * 1000);
  else date = new Date(startTime);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Earliest and latest kickoff among games, or null when none has a kickoff.
 * @returns {{ min: Date, max: Date } | null}
 */
export const dateSpan = (games) => {
  const times = (games || []).map((g) => toDate(g.startTime)).filter(Boolean).map((d) => d.getTime());
  if (times.length === 0) return null;
  return { min: new Date(Math.min(...times)), max: new Date(Math.max(...times)) };
};

const formatDay = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** "Oct 17", "Oct 9–10", or "Oct 30–Nov 1" for a span from dateSpan. */
export const formatSpan = (span) => {
  if (!span) return '';
  const from = formatDay(span.min);
  const to = formatDay(span.max);
  if (from === to) return from;
  if (span.min.getMonth() === span.max.getMonth() && span.min.getFullYear() === span.max.getFullYear()) {
    return `${from}–${span.max.getDate()}`;
  }
  return `${from}–${to}`;
};

/**
 * Default "Import to Week": the working week, or the next one once every game
 * in the working week has kicked off. An empty working week stays put — that
 * is the normal "fill a fresh week" case.
 */
export const defaultTargetWeek = (week, existingGames, now, maxWeek) => {
  const current = Number(week);
  if (!Number.isInteger(current) || current < 1) return week || '';
  const kickoffs = (existingGames || []).map((g) => toDate(g.startTime)).filter(Boolean);
  if (kickoffs.length === 0) return current;
  const allKickedOff = kickoffs.every((d) => d.getTime() <= now);
  return allKickedOff ? Math.min(current + 1, maxWeek) : current;
};

/**
 * Review-step games (apiGameId, homeTeamId, awayTeamId) that a week already
 * holds, matched by API id or by matchup in either home/away arrangement.
 */
export const findDuplicates = (importGames, weekGames) =>
  importGames.filter((game) =>
    (weekGames || []).some((existing) =>
      (game.apiGameId && existing.apiGameId === game.apiGameId) ||
      (existing.homeTeam === game.homeTeamId && existing.awayTeam === game.awayTeamId) ||
      (existing.homeTeam === game.awayTeamId && existing.awayTeam === game.homeTeamId)
    )
  );

/**
 * The Saturday (local midnight) of the Wed–Tue game week a kickoff falls in,
 * the same weeks the importer's date range uses. Tuesday-night and Wednesday
 * games belong to the Saturday nearest them.
 */
export const weekSaturday = (date) => {
  const saturday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  let offset = (6 - saturday.getDay() + 7) % 7; // Sat 0, Fri 1, … Sun 6
  if (offset > 3) offset -= 7; // Sun–Tue look back to the previous Saturday
  saturday.setDate(saturday.getDate() + offset);
  return saturday;
};

// The game week most of these games fall in, as its Saturday's time value.
const mainWeekSaturday = (games) => {
  const counts = new Map();
  for (const g of games) {
    const date = toDate(g.startTime);
    if (!date) continue;
    const key = weekSaturday(date).getTime();
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let best = null;
  for (const [key, count] of counts) {
    if (best === null || count > counts.get(best) || (count === counts.get(best) && key < best)) best = key;
  }
  return best;
};

/**
 * Compares the kickoffs being imported with the target week's existing games.
 * Returns null when every imported game falls in the same Wed–Tue game week
 * as the target week's games (or the target week has no dated games to
 * compare against). Otherwise returns both date spans and the week the games
 * probably belong in (null when that can't be told or would fall outside
 * 1..maxWeek).
 * @returns {{ importSpan, targetSpan, suggestedWeek: number | null } | null}
 */
export const weekMismatch = (importGames, targetGames, targetWeek, maxWeek) => {
  const targetSaturday = mainWeekSaturday(targetGames || []);
  const importSpan = dateSpan(importGames);
  if (targetSaturday === null || !importSpan) return null;

  const outside = importGames.some((g) => {
    const date = toDate(g.startTime);
    return date && weekSaturday(date).getTime() !== targetSaturday;
  });
  if (!outside) return null;

  // Whole weeks between the two Saturdays (rounded for daylight-saving shifts).
  const weeks = Math.round((mainWeekSaturday(importGames) - targetSaturday) / (7 * DAY_MS));
  const suggestedWeek = Number(targetWeek) + weeks;

  return {
    importSpan,
    targetSpan: dateSpan(targetGames),
    suggestedWeek: weeks !== 0 && suggestedWeek >= 1 && suggestedWeek <= maxWeek ? suggestedWeek : null,
  };
};
